import { Box, ListItem, ListItemText, useTheme } from "@mui/material";
import React, { useState } from "react";

import { EditFieldButton, RobotoTypography } from "@/shared/components";
import {
  InvocationInput,
  InvocationInputs,
  invocationInputToYaml,
} from "@/shared/domain/actions";
import { UpdateScheduledTriggerRequest } from "@/shared/domain/actions/TriggerRecord";
import { QueryTarget } from "@/shared/domain/query";

import { TriggerInvocationInput } from "./TriggerInvocationInput";

interface EditableInvocationInputProps {
  value: InvocationInput | null;
  onChange: (updates: UpdateScheduledTriggerRequest) => void;
  setErrorText: (error: string) => void;
}

function extractEditableQuery(
  input: InvocationInput | null,
): [QueryTarget?, string?] {
  if (input === null) {
    return [undefined, undefined];
  }

  if (Array.isArray(input.files) || Array.isArray(input.topics)) {
    // We currently support editing only a single file or topic query in the UI.
    return [undefined, undefined];
  }

  if (input.files !== null && input.topics !== null) {
    // We currently support editing only a single file or topic query in the UI.
    return [undefined, undefined];
  }

  if (input.files !== null) {
    const { query, ids, names, paths } = input.files;

    if (!!query && !ids && !names && !paths) {
      return [QueryTarget.Files, query];
    }
  } else if (input.topics !== null) {
    const { query, ids, names } = input.topics;

    if (!!query && !ids && !names) {
      return [QueryTarget.Topics, query];
    }
  }

  // We can only handle file or topic queries for now.
  return [undefined, undefined];
}

function canEdit(input: InvocationInput | null): boolean {
  if (input === null) {
    // We can set it to something using this component.
    return true;
  }

  const [target, query] = extractEditableQuery(input);
  return target !== undefined && query !== undefined;
}

/**
 * Note (ruslan@): as of 9-2025, this component presents a simplified
 * user experience around editing the invocation input of a trigger.
 * In particular, it makes the assumption that the input is a single RoboQL
 * query, with a query target (e.g. files) that's supported on the action
 * backend.
 *
 * The `InvocationInput` type supports further "complications", like specifying
 * both a file and a topic query, multiple queries of each type, entities by name
 * or ID, etc. If a user has a trigger configured with such an invocation input spec,
 * we'll disable editing, so that we don't clobber that spec on save.
 */
export const EditableInvocationInput: React.FC<
  EditableInvocationInputProps
> = ({ value, onChange, setErrorText }) => {
  const theme = useTheme();

  const [extractedTarget, extractedQuery] = extractEditableQuery(value);
  const isFileQuery = extractedTarget === QueryTarget.Files;

  const [showEditableIcon, setShowEditableIcon] = useState(false);
  const [editable, setEditable] = useState(false);

  const [queryTarget, setQueryTarget] = useState<QueryTarget>(
    extractedTarget ?? QueryTarget.Files,
  );
  const [fileQuery, setFileQuery] = useState<string | undefined>(
    isFileQuery ? extractedQuery : undefined,
  );
  const [topicQuery, setTopicQuery] = useState<string | undefined>(
    isFileQuery ? undefined : extractedQuery,
  );

  const updateTrigger = (): void => {
    setErrorText("");

    let newInput: InvocationInput | null = null;
    switch (queryTarget) {
      case QueryTarget.Files:
        if (fileQuery !== undefined && fileQuery.trim().length > 0) {
          newInput = InvocationInputs.fileQuery(fileQuery.trim());
        }
        break;

      case QueryTarget.Topics:
        if (topicQuery !== undefined && topicQuery.trim().length > 0) {
          newInput = InvocationInputs.topicQuery(topicQuery.trim());
        }
        break;

      default:
        throw Error(`Support for querying '${queryTarget}' is forthcoming.`);
    }

    onChange({
      invocation_input: newInput,
    });
  };

  return (
    <ListItem
      onMouseOver={() => {
        setShowEditableIcon(canEdit(value));
      }}
      onMouseLeave={() => {
        setShowEditableIcon(false);
      }}
    >
      <ListItemText
        primary={
          <div>
            <span>Invocation Input</span>

            <EditFieldButton
              editable={editable}
              showEditableIcon={showEditableIcon}
              onClick={() => {
                updateTrigger();
                setEditable(!editable);
              }}
            />
          </div>
        }
        secondaryTypographyProps={{
          component: "div",
        }}
        secondary={
          <div>
            {!editable && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: theme.spacing(1),
                }}
              >
                {value === null ? (
                  "None"
                ) : (
                  <>
                    {!canEdit(value) && (
                      <RobotoTypography variant="caption">
                        Only inputs defined by a single RoboQL query are
                        currently editable on this page.
                      </RobotoTypography>
                    )}

                    <pre style={{ whiteSpace: "pre-wrap" }}>
                      {invocationInputToYaml(value)}
                    </pre>
                  </>
                )}
              </Box>
            )}

            {editable && (
              <TriggerInvocationInput
                queryTarget={queryTarget}
                onQueryTargetChanged={(newTarget: QueryTarget) => {
                  setQueryTarget(newTarget);
                }}
                roboqlQuery={
                  queryTarget === QueryTarget.Files ? fileQuery : topicQuery
                }
                onRoboqlQueryChanged={(newQuery: string | undefined) => {
                  if (queryTarget === QueryTarget.Files) {
                    setFileQuery(newQuery);
                  } else {
                    setTopicQuery(newQuery);
                  }
                }}
              />
            )}
          </div>
        }
      />
    </ListItem>
  );
};
